import React, { useState, useEffect, useRef } from 'react';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage, useFormikContext } from 'formik';
import * as Yup from 'yup';
import {
    Row,
    Col,
    Card,
    CardBody,
    CardHeader,
    Button,
    FormGroup,
    Label,
    Input,
    Alert,
    Table,
    Badge,
    Spinner,
    Container,
    Modal,
    ModalHeader,
    ModalBody
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaMoneyBillWave, FaPlus, FaMinus, FaList, FaUser, FaCalendarAlt, FaEdit, FaTrash, FaShieldAlt, FaArrowUp, FaArrowDown, FaBalanceScale, FaArrowLeft, FaSearch } from 'react-icons/fa';
import { reglementActions } from '../../sagas/reglementSlice';
import { formatPrice, preventScroll } from '../../helpers/utility';
import Select from 'react-select';
import DocumentSelectionModal from './DocumentSelectionModal';
import './ReglementForm.css'; // Assuming you have some custom styles
import { clientActions } from '../../sagas/clientSlice';
import { deliveryActions } from '../../sagas/deliveryNoteSlice';
import { invoiceActions } from '../../sagas/invoiceSlice';
import { companyActions } from '../../sagas/companySlice';
import { useLocation, useNavigate } from 'react-router-dom';
import axiosInstance from '../../services/api_helper';

const ReglementForm = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { invoices: facturesImpayees, loading: loadingInvoices } = useSelector((state) => state.invoices);
    const { list: bonLivraisonsNonRegles, loading: loadingDeliveryNotes } = useSelector((state) => state.deliveryNotes);

    const { clients } = useSelector(state => state.clients);
    const { company: defaultCompany, role } = useSelector((state) => state.auth);
    const { companies } = useSelector((state) => state.companies);

    const navigate = useNavigate();
    const location = useLocation();
    const reglement = location.state?.reglement || null;
    const quickPayment = location.state?.quickPayment || null;
    const quickDocuments = useMemo(
        () => (Array.isArray(quickPayment?.documents) ? quickPayment.documents : []),
        [quickPayment?.documents]
    );

    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedChantier, setSelectedChantier] = useState(null);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [typeReglement, setTypeReglement] = useState(quickPayment?.typeReglement || 'AVANCE');
    const [isCautionPayment, setIsCautionPayment] = useState((reglement?.typeReglement || quickPayment?.typeReglement) === 'CAUTION');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [showAvanceModal, setShowAvanceModal] = useState(false);
    const [avanceDocFilter, setAvanceDocFilter] = useState("NONE");
    const [utilisationAvances, setUtilisationAvances] = useState([]);
    const [availableAdvances, setAvailableAdvances] = useState([]);
    const [selectedAdvanceIds, setSelectedAdvanceIds] = useState([]);
    const [loadingAdvances, setLoadingAdvances] = useState(false);
    const [documentFilters, setDocumentFilters] = useState({ query: '', amount: '' });
    const [documentSort, setDocumentSort] = useState({ key: 'date', direction: 'desc' });
    const [documentsInitialized, setDocumentsInitialized] = useState(false);
    const isDocumentBasedReglement = selectedDocuments.length > 0 || typeReglement === 'FACTURE' || typeReglement === 'BON_LIVRAISON';


    const { loading: loadingReglements, error: errorReglements, echeances, cautions } = useSelector((state) => state.reglements);
    const [showEcheanceModal, setShowEcheanceModal] = useState(false);
    const [showCautionModal, setShowCautionModal] = useState(false);
    const formikRef = useRef(null);
    const [filters, setFilters] = useState({
        clientId: null,
        dateFrom: null,
        dateTo: null,
        statut: "EN_ATTENTE"
    });
    const isSuperAdmin = role === 'ROLE_SUPER_ADMIN';
    const canEdit = isSuperAdmin || (reglement && reglement.statut === 'EN_ATTENTE') || !reglement;
    const isQuickPayment = Boolean(quickPayment && !reglement);
    const isCompanyFieldDisabled = !canEdit || isDocumentBasedReglement;
    const resolvedDefaultCompanyId =
        reglement?.companyId ||
        reglement?.company?.id ||
        quickPayment?.companyId ||
        defaultCompany?.id ||
        companies?.[0]?.id ||
        null;

    // Validation schema
    const validationSchema = Yup.object().shape({
        companyId: Yup.number()
            .transform((value, originalValue) => (originalValue === '' ? null : value))
            .nullable()
            .required(t('company.companyRequired', 'Entreprise requise')),
        clientId: Yup.number().required(t('reglement.clientRequired')),
        montantRegle: Yup.number()
            .min(0, t('reglement.montantPositive'))
            .required(t('reglement.montantRequired')),
        dateReglement: Yup.date().required(t('reglement.dateReglementRequired')),
        modeReglement: Yup.string().required(t('reglement.modeReglementRequired')),
        statut: Yup.string().required(t('reglement.statutRequired')),

    });

    const extractDocumentNumeros = (reglement) => {
        if (!reglement) return [];

        const source =
            reglement.typeReglement === 'FACTURE'
                ? reglement.numFactures
                : reglement.numBonLivraisons;

        if (!source) return [];

        return source
            .split(',')
            .map(n => n.trim())
            .filter(Boolean);
    };

    const normalizeNumero = (value) => value?.toString().trim().toUpperCase() || '';

    const buildSelectedDocumentsFromAllocations = (allocations = []) => {
        if (!Array.isArray(allocations) || allocations.length === 0) {
            return [];
        }

        return allocations
            .filter(allocation => normalizeNumero(allocation?.numeroDocument))
            .map((allocation, index) => {
                const montantTotal = Number(allocation?.montantDocument || 0);
                const montantRestant = Number(allocation?.montantRestant || 0);
                const montantPaye = Math.max(
                    montantTotal - montantRestant,
                    Number(allocation?.montantAlloue || 0)
                );

                return {
                    id: allocation?.documentId || allocation?.id || `${allocation?.numeroDocument}-${index}`,
                    numero: allocation?.numeroDocument,
                    montantTotalTTC: montantTotal,
                    montantRegle: montantPaye,
                    montantPaye,
                    createdAt: allocation?.dateDocument || allocation?.dateOperation || allocation?.createdAt || null,
                    dateFacture: allocation?.dateDocument || null,
                    status: allocation?.statutDocument || allocation?.statut || null,
                    statut: allocation?.statutDocument || allocation?.statut || null,
                    clientNom: reglement?.nomClient || '',
                };
            });
    };


    // Initial values
    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        companyId: resolvedDefaultCompanyId,
        clientId: reglement?.clientId || quickPayment?.clientId || '',
        numFactures: reglement?.numFactures || (quickPayment?.typeReglement === 'FACTURE' ? quickDocuments.map(doc => doc.numero).join(',') : ''),
        numBonLivraisons: reglement?.numBonLivraisons || (quickPayment?.typeReglement === 'BON_LIVRAISON' ? quickDocuments.map(doc => doc.numero).join(',') : ''),
        typeReglement: reglement?.typeReglement || quickPayment?.typeReglement || 'FACTURE',
        montantRegle: reglement?.montantRegle || quickPayment?.montantRegle || 0,
        montantAffecte: reglement?.montantAffecte || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        dateEncaissement: reglement?.dateEncaissement || '',
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        commentaire: reglement?.commentaire || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
        motifAvance: reglement?.motifAvance || '',
        dateValiditeAvance: reglement?.dateValiditeAvance || '',
        commentaireAvance: reglement?.commentaireAvance || '',
    };

    const modeReglementOptions = [
        { value: 'ESPECES', label: t('reglement.especes') },
        { value: 'CHEQUE', label: t('reglement.cheque') },
        { value: 'TRAITE', label: t('reglement.traite') },
        { value: 'VIREMENT', label: t('reglement.virement') },
        { value: 'CARTE_BANCAIRE', label: t('reglement.carteBancaire') },
    ];

    const clientOptions = clients?.map(client => ({
        solde: client.solde,
        totalDebit: client.totalDebit,
        totalCredit: client.totalCredit,
        value: client.id,
        label: client.typeClient === 'PARTICULIER'
            ? `${client.nom} ${client.prenom || ''} `
            : client.entreprise,
        chantiers: client.chantiers
    })) ?? [];

    // Create chantier options from selected client's chantiers
    const chantierOptions = selectedClient?.chantiers?.map(chantier => ({
        value: chantier.id,
        label: `${chantier.code} - ${chantier.nom}`,
        ...chantier
    })) || [];

    const getTotalMontant = (items) => {
        return items.reduce((total, item) => total + item.montantRegle, 0);
    };

    const getDocumentTotalAmount = (document) =>
        Number(document?.montantTotalTTC ?? document?.montantTTC ?? 0);

    const getDocumentPaidAmount = (document) =>
        Number(document?.montantRegle ?? document?.montantPaye ?? 0);

    const getDocumentRemainingAmount = (document) =>
        getDocumentTotalAmount(document) - getDocumentPaidAmount(document);

    const calculateDocumentsAmount = (documents = []) =>
        documents.reduce((total, doc) => total + getDocumentRemainingAmount(doc), 0);

    const totalAvailableAdvances = useMemo(
        () => availableAdvances.reduce((total, avance) => total + Number(avance.montantDisponible || 0), 0),
        [availableAdvances]
    );

    const buildSelectedAdvanceUsages = (documents = selectedDocuments) => {
        let remaining = calculateDocumentsAmount(documents);
        if (remaining <= 0 || !availableAdvances.length || !selectedAdvanceIds.length) {
            return [];
        }

        return availableAdvances.reduce((usages, avance) => {
            if (remaining <= 0) return usages;
            if (!selectedAdvanceIds.includes(avance.id)) return usages;
            const disponible = Number(avance.montantDisponible || 0);
            const montantUtilise = Math.min(disponible, remaining);
            if (montantUtilise > 0) {
                usages.push({
                    avanceId: avance.id,
                    montantUtilise,
                    commentaire: `Utilisation avance ${avance.referenceReglement || avance.reglementId || avance.id}`
                });
                remaining -= montantUtilise;
            }
            return usages;
        }, []);
    };

    const calculateAdvanceUsageAmount = (documents = selectedDocuments) =>
        buildSelectedAdvanceUsages(documents).reduce((total, usage) => total + Number(usage.montantUtilise || 0), 0);

    const calculateCashAmountAfterAdvances = (documents = selectedDocuments) =>
        Math.max(0, calculateDocumentsAmount(documents) - calculateAdvanceUsageAmount(documents));

    const syncMontantRegleFromDocuments = (documents, setFieldValue = formikRef.current?.setFieldValue) => {
        if (!reglement && documents.length > 0) {
            setFieldValue?.('montantRegle', calculateCashAmountAfterAdvances(documents));
        }
    };

    const resolveDocumentCompanyId = (document) =>
        document?.companyId ||
        document?.company?.id ||
        null;

    const resolveDocumentCompanyLabel = (document) =>
        document?.company?.name ||
        document?.companyName ||
        document?.nomEntreprise ||
        '-';

    const syncCompanyFromDocuments = (documents, setFieldValue = formikRef.current?.setFieldValue) => {
        if (!documents || documents.length === 0) {
            return;
        }

        const nextCompanyId = Array.isArray(documents) && documents.length > 0
            ? resolveDocumentCompanyId(documents[0])
            : null;

        setSelectedCompanyId(nextCompanyId);
        if (setFieldValue) {
            setFieldValue('companyId', nextCompanyId || '');
        }
    };

    const compactSelectStyles = {
        control: (base) => ({
            ...base,
            minHeight: '34px',
            fontSize: '0.875rem',
        }),
        valueContainer: (base) => ({
            ...base,
            padding: '0 8px',
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base) => ({
            ...base,
            height: '34px',
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: '4px',
        }),
        menuPortal: base => ({ ...base, zIndex: 9999 }),
    };

    useEffect(() => {
        dispatch(clientActions.search({
            query: ""
        }));

        if (!companies || companies.length === 0) {
            dispatch(companyActions.getAll({}));
        }
    }, [companies, dispatch]);

    useEffect(() => {
        setSelectedCompanyId(resolvedDefaultCompanyId);
    }, [resolvedDefaultCompanyId]);

    useEffect(() => {
        if (!isQuickPayment) {
            return;
        }

        setTypeReglement(quickPayment.typeReglement || 'FACTURE');
        setIsCautionPayment(quickPayment.typeReglement === 'CAUTION');
        setSelectedCompanyId(quickPayment.companyId || resolvedDefaultCompanyId);
        setSelectedDocuments(quickDocuments);
        setDocumentsInitialized(true);
    }, [isQuickPayment, quickPayment, quickDocuments, resolvedDefaultCompanyId]);

    useEffect(() => {
        if (!isQuickPayment || !quickPayment?.clientId || !clients?.length) {
            return;
        }

        const client = clients.find(item => Number(item.id) === Number(quickPayment.clientId));
        if (!client) {
            return;
        }

        setSelectedClient({
            value: client.id,
            label: client.typeClient === 'PARTICULIER'
                ? `${client.nom || ''} ${client.prenom || ''}`.trim()
                : client.entreprise,
            solde: client.solde,
            totalDebit: client.totalDebit,
            totalCredit: client.totalCredit,
            chantiers: client.chantiers
        });

        if (quickPayment.chantierId && client.chantiers) {
            const chantier = client.chantiers.find(item => Number(item.id) === Number(quickPayment.chantierId));
            setSelectedChantier(chantier ? {
                value: chantier.id,
                label: `${chantier.code} - ${chantier.nom}`,
                ...chantier
            } : null);
        }

        setFilters(f => ({ ...f, clientId: client.id }));
        dispatch(reglementActions.echeanceClient({ clientId: client.id, statut: "EN_ATTENTE" }));
        dispatch(reglementActions.cautionClient({ clientId: client.id, statut: "EN_ATTENTE" }));
    }, [clients, dispatch, isQuickPayment, quickPayment?.chantierId, quickPayment?.clientId]);


    // Initialize form with existing reglement data
    useEffect(() => {
        if (reglement) {
            // Set selected client
            const client = clients.find(c => c.id === reglement.clientId);
            if (client) {
                setSelectedClient({
                    value: client.id,
                    label: client.typeClient === 'PARTICULIER'
                        ? `${client.prenom} ${client.nom}`
                        : client.entreprise,
                    solde: client.solde,
                    totalDebit: client.totalDebit,
                    totalCredit: client.totalCredit,
                    chantiers: client.chantiers
                });

                // Set selected chantier if exists
                if (reglement.chantierId && client.chantiers) {
                    const chantier = client.chantiers.find(ch => ch.id === reglement.chantierId);
                    if (chantier) {
                        setSelectedChantier({
                            value: chantier.id,
                            label: `${chantier.code} - ${chantier.nom}`,
                            ...chantier
                        });
                    }
                }

                dispatch(reglementActions.echeanceClient({ ...filters, clientId: client.id }));
                dispatch(reglementActions.cautionClient({ ...filters, clientId: client.id }));
            }

            setTypeReglement(reglement.typeReglement);
            setIsCautionPayment(reglement.typeReglement === 'CAUTION');

        }
    }, [reglement, clients]);

    useEffect(() => {
        if (
            !reglement ||
            documentsInitialized ||
            !selectedClient
        ) return;

        const documentsFromAllocations = buildSelectedDocumentsFromAllocations(reglement.allocations);
        if (documentsFromAllocations.length > 0) {
            setSelectedDocuments(documentsFromAllocations);
            setDocumentsInitialized(true);
            return;
        }

        const sourceDocuments =
            typeReglement === 'FACTURE'
                ? facturesImpayees
                : bonLivraisonsNonRegles;

        if (!Array.isArray(sourceDocuments) || sourceDocuments.length === 0) {
            return;
        }

        const numeros = extractDocumentNumeros(reglement);

        if (numeros.length === 0) {
            setDocumentsInitialized(true);
            return;
        }

        const matchedDocuments = sourceDocuments.filter(doc =>
            numeros.some(numero => normalizeNumero(numero) === normalizeNumero(doc.numero))
        );
        if (matchedDocuments.length > 0) {
            setSelectedDocuments(matchedDocuments);
        }

        setDocumentsInitialized(true);
    }, [
        reglement,
        typeReglement,
        selectedClient,
        facturesImpayees,
        bonLivraisonsNonRegles,
        documentsInitialized
    ]);


    // Load documents when client changes
    useEffect(() => {
        if (selectedClient) {
            const companyIdFilter = selectedCompanyId ?? null;
            dispatch(invoiceActions.listWithSubInvoices({
                clientId: selectedClient.value,
                companyId: companyIdFilter,
            }));
            dispatch(deliveryActions.list({
                query: clients.find(c => c.id === selectedClient.value)?.code || '',
                companyId: companyIdFilter,
                sansFacture: true,
            }));
        }
    }, [selectedClient, dispatch, clients, selectedCompanyId]);

    useEffect(() => {
        if (!selectedClient?.value || !selectedCompanyId) {
            setAvailableAdvances([]);
            setSelectedAdvanceIds([]);
            setLoadingAdvances(false);
            return;
        }

        let cancelled = false;
        setLoadingAdvances(true);
        setAvailableAdvances([]);
        setSelectedAdvanceIds([]);
        axiosInstance.get(`/api/avances/client/${selectedClient.value}/disponibles`, {
            params: { companyId: selectedCompanyId }
        })
            .then(({ data }) => {
                if (!cancelled) {
                    setAvailableAdvances(Array.isArray(data) ? data : []);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setAvailableAdvances([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingAdvances(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [selectedClient?.value, selectedCompanyId]);

    useEffect(() => {
        if (!reglement && selectedDocuments.length > 0) {
            formikRef.current?.setFieldValue('montantRegle', calculateCashAmountAfterAdvances(selectedDocuments));
        }
    }, [availableAdvances, reglement, selectedAdvanceIds, selectedDocuments]);

    const toggleDocumentSelection = (document, isSelected, setFieldValue) => {
        if (isSelected) {
            setIsCautionPayment(false);
            setTypeReglement(document.paymentDocumentType || typeReglement);
        }
        setSelectedDocuments(prev => {
            let nextDocuments;

            if (isSelected) {
                if (prev.some(doc => doc.id === document.id)) {
                    nextDocuments = prev;
                } else {
                    nextDocuments = [...prev, document];
                }
            } else {
                nextDocuments = prev.filter(doc => doc.id !== document.id);
            }

            syncCompanyFromDocuments(nextDocuments, setFieldValue);
            syncMontantRegleFromDocuments(nextDocuments, setFieldValue);
            return nextDocuments;
        });
    };

    const handleDocumentsSelected = (documents, setFieldValue) => {
        if (documents.length > 0) {
            setIsCautionPayment(false);
            setTypeReglement(documents[0].paymentDocumentType || typeReglement);
        }
        setSelectedDocuments(documents);
        syncCompanyFromDocuments(documents, setFieldValue);
        syncMontantRegleFromDocuments(documents, setFieldValue);
    };

    // Calculate total amount
    const calculateTotalAmount = () => calculateDocumentsAmount(selectedDocuments);

    // Remove document from selection
    const removeDocument = (documentId, setFieldValue) => {
        setSelectedDocuments(prev => {
            const nextDocuments = prev.filter(doc => doc.id !== documentId);
            syncCompanyFromDocuments(nextDocuments, setFieldValue);
            syncMontantRegleFromDocuments(nextDocuments, setFieldValue);
            return nextDocuments;
        });
    };


    const getSelectedInvoices = () =>
        selectedDocuments.filter(doc => doc.paymentDocumentType === 'FACTURE' || (typeReglement === 'FACTURE' && !doc.paymentDocumentType));

    const getSelectedDeliveryNotes = () =>
        selectedDocuments.filter(doc => doc.paymentDocumentType === 'BON_LIVRAISON' || (typeReglement === 'BON_LIVRAISON' && !doc.paymentDocumentType));

    const resolveEffectiveTypeReglement = () => {
        if (isCautionPayment && selectedDocuments.length === 0) return 'CAUTION';
        if (selectedDocuments.length === 0) return 'AVANCE';
        return getSelectedInvoices().length > 0 ? 'FACTURE' : 'BON_LIVRAISON';
    };

    const getBusinessErrorMessage = (error) =>
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t('common.errorOccurred');

    const MontantRegleAutoSync = ({ shouldSync, amount }) => {
        const { setFieldValue } = useFormikContext();
        const previousAmountRef = useRef(amount);

        useEffect(() => {
            if (!shouldSync) {
                previousAmountRef.current = amount;
                return;
            }

            if (Number(previousAmountRef.current || 0) !== Number(amount || 0)) {
                setFieldValue('montantRegle', amount);
            }
            previousAmountRef.current = amount;
        }, [amount, setFieldValue, shouldSync]);

        return null;
    };

    const handleSubmit = (values, { setSubmitting }) => {
        const effectiveTypeReglement = resolveEffectiveTypeReglement();
        const selectedInvoices = getSelectedInvoices();
        const selectedDeliveryNotes = getSelectedDeliveryNotes();
        const automaticAdvanceUsages =
            effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION'
                ? []
                : buildSelectedAdvanceUsages(selectedDocuments);
        const totalAvancesUtilisees = automaticAdvanceUsages.reduce((total, usage) => total + Number(usage.montantUtilise || 0), 0);
        const isEcheancePayment = ['CHEQUE', 'TRAITE'].includes(values.modeReglement);
        const payload = {
            data: {
                ...values,
                numeroCheque: isEcheancePayment ? values.numeroCheque : null,
                banque: isEcheancePayment ? values.banque : null,
                dateEcheance: isEcheancePayment ? values.dateEcheance : null,
                companyId: values.companyId ? Number(values.companyId) : null,
                typeReglement: effectiveTypeReglement,
                chantierId: selectedChantier ? selectedChantier.value : null,
                numFactures: selectedInvoices.map(doc => doc.numero).filter(Boolean).join(','),
                numBonLivraisons: selectedDeliveryNotes.map(doc => doc.numero).filter(Boolean).join(','),
                montantRegle: Number(values.montantRegle || 0),
                montantAffecte: effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION' ? 0 : calculateTotalAmount(),
                documentsIds: effectiveTypeReglement === 'AVANCE' || effectiveTypeReglement === 'CAUTION' ? [] : selectedDocuments.map(doc => doc.id),
                estAvance: effectiveTypeReglement === 'AVANCE',

                utilisationAvances: automaticAdvanceUsages.length > 0 ? automaticAdvanceUsages : utilisationAvances,
                montantAvancesUtilisees: totalAvancesUtilisees,
                montantReglementNet: values.montantRegle || 0,
                // Champs spÃ©cifiques aux avances
                dateValiditeAvance: effectiveTypeReglement === 'AVANCE' ? values.dateValiditeAvance : null,
                commentaireAvance: effectiveTypeReglement === 'AVANCE' ? values.commentaireAvance : null,
            },
            onSuccess: (data) => {
                Swal.fire({
                    icon: 'success',
                    title: t('common.success'),
                    text: reglement ? t('reglement.updateSuccess') :
                        effectiveTypeReglement === 'AVANCE' ? t('reglement.avanceCreateSuccess') :
                            t('reglement.createSuccess'),
                });
                dispatch(reglementActions.searchRequest(filters));
                navigate("/sales-module/payments");
            },
            onError: (error) => {
                Swal.fire({
                    icon: 'error',
                    title: t('common.error'),
                    text: getBusinessErrorMessage(error),
                });
                setSubmitting(false);
            }
        };

        if (reglement) {
            const updateAction =
                isSuperAdmin && reglement.statut === 'ENCAISSE'
                    ? reglementActions.updateEncaisseRequest
                    : reglementActions.updateRequest;

            dispatch(updateAction({ ...payload, id: reglement.id }));
        } else {
            dispatch(reglementActions.createRequest(payload));
        }
    };

    const availableDocuments = [
        ...facturesImpayees
            .filter(f => f.statut !== 'ANNULEE' && f.statut !== 'PAYEE')
            .map(doc => ({ ...doc, paymentDocumentType: 'FACTURE' })),
        ...bonLivraisonsNonRegles
            .map(doc => ({ ...doc, paymentDocumentType: 'BON_LIVRAISON' })),
    ];

    const filteredAvailableDocuments = (Array.isArray(availableDocuments) ? availableDocuments : []).filter((document) => {
        const search = documentFilters.query.trim().toLowerCase();
        const hasAmountFilter = documentFilters.amount !== '' && documentFilters.amount !== null;
        const amount = Number(documentFilters.amount || 0);
        const remainingAmount = getDocumentRemainingAmount(document);

        const matchesSearch = !search || [
            document.numero,
            document.reference,
            document.clientNom,
            resolveDocumentCompanyLabel(document),
        ].some(value => value?.toString().toLowerCase().includes(search));

        const matchesAmount = !hasAmountFilter || Math.abs(remainingAmount - amount) < 0.0001;

        return matchesSearch && matchesAmount && remainingAmount > 0;
    });

    const getDocumentSortValue = (document, key) => {
        switch (key) {
            case 'numero':
                return document.numero || '';
            case 'date': {
                const date = document.dateOperation || document.dateFacture || document.createdAt;
                return date ? new Date(date).getTime() || 0 : 0;
            }
            case 'total':
                return getDocumentTotalAmount(document);
            case 'paid':
                return getDocumentPaidAmount(document);
            case 'remaining':
                return getDocumentRemainingAmount(document);
            case 'status':
                return document.statut || document.status || '';
            default:
                return '';
        }
    };

    const sortedAvailableDocuments = [...filteredAvailableDocuments].sort((a, b) => {
        const left = getDocumentSortValue(a, documentSort.key);
        const right = getDocumentSortValue(b, documentSort.key);

        if (typeof left === 'number' && typeof right === 'number') {
            return documentSort.direction === 'asc' ? left - right : right - left;
        }

        return documentSort.direction === 'asc'
            ? String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' })
            : String(right).localeCompare(String(left), undefined, { numeric: true, sensitivity: 'base' });
    });

    const handleDocumentSort = (key) => {
        setDocumentSort(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const renderDocumentSortHeader = (label, key) => (
        <th
            onClick={() => handleDocumentSort(key)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
        >
            {label}{documentSort.key === key ? (documentSort.direction === 'asc' ? ' ▲' : ' ▼') : ''}
        </th>
    );

    const selectableDocuments = sortedAvailableDocuments.filter(document => getDocumentRemainingAmount(document) > 0);
    const selectedVisibleDocuments = selectedDocuments.filter(selected =>
        selectableDocuments.some(document => document.id === selected.id)
    );
    const allFilteredDocumentsSelected =
        selectableDocuments.length > 0 &&
        selectedVisibleDocuments.length === selectableDocuments.length;
    const hasInvoiceDocuments = filteredAvailableDocuments.some(document => document.paymentDocumentType === 'FACTURE');

	    const documentLoading = loadingInvoices || loadingDeliveryNotes;





    return (
        <Container fluid className="reglement-form payment-form-compact-page payment-form-modern">
            <Formik
                innerRef={formikRef}
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
                    <Form className="payment-form-shell">
                        <div className="payment-form-header">
                            <div>
                                <div className="payment-form-eyebrow">Paiement client</div>
                                <h2>{reglement ? t('reglement.edit') : t('reglement.new')}</h2>
                                <p>Selectionnez le client, consultez les impayes, puis saisissez le reglement ou une avance.</p>
                            </div>
                            <div className="payment-header-actions">
                                <Badge color={reglement?.statut === 'ENCAISSE' ? 'success' : reglement?.statut === 'ANNULE' ? 'danger' : 'info'}>
                                    {reglement?.statut || 'NOUVEAU'}
                                </Badge>
                                <Button
                                    color="secondary"
                                    outline
                                    size="sm"
                                    onClick={() => navigate(-1)}
                                    className="d-flex align-items-center"
                                >
                                    <FaArrowLeft className="me-2" />
                                    {t('common.back', 'Retour')}
                                </Button>
                                {canEdit && (
                                    <Button
                                        type="submit"
                                        color="primary"
                                        size="sm"
                                        className="payment-primary-action"
                                        disabled={
                                            isSubmitting ||
                                            (!selectedClient) ||
                                            ((!values.montantRegle || values.montantRegle <= 0) && calculateAdvanceUsageAmount(selectedDocuments) <= 0)
                                        }
                                    >
                                        {isSubmitting && <Spinner size="sm" className="me-2" />}
                                        <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                        {reglement ? t('common.update') : t('common.create')}
                                    </Button>
                                )}
                            </div>
                        </div>
                        <MontantRegleAutoSync
                            shouldSync={!reglement && selectedDocuments.length > 0}
                            amount={calculateCashAmountAfterAdvances(selectedDocuments)}
                        />
                        <div className="payment-workspace">
                        <section className="payment-main-pane">
                        <Row className="payment-form-left-item payment-identity-shell g-0">
                            {/* Section Client et Type */}
                            <Col md={12}>
                                <Card className="payment-form-panel payment-form-identity-panel mb-3">
                                    <CardHeader className="payment-form-section-header">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">
                                                <FaMoneyBillWave className="me-2" />
                                                {t('reglement.informationsGenerales')}
                                            </h5>
                                        </div>
                                    </CardHeader>
                                    <CardBody className="py-3">
                                        <Row className="g-2 align-items-end">
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label for="clientId" className="fw-bold text-secondary small mb-1">
                                                        <FaUser className="me-1" />
                                                        {t('reglement.client')} *
                                                    </Label>
                                                    <Select
                                                        options={clientOptions}
                                                        value={selectedClient}
                                                        onChange={(option) => {
                                                            if (canEdit) {
                                                                setSelectedClient(option);
                                                                setSelectedChantier(null);
                                                                setFieldValue('clientId', option?.value || '');
                                                                setSelectedDocuments([]);
                                                                setIsCautionPayment(false);
                                                                setDocumentFilters({ query: '', amount: '' });
                                                                setDocumentsInitialized(false);
                                                                if (option) {
                                                                    dispatch(reglementActions.echeanceClient({ ...filters, clientId: option.value }));
                                                                    dispatch(reglementActions.cautionClient({ ...filters, clientId: option.value }));
                                                                    setFilters(f => ({ ...f, clientId: option.value }));
                                                                }
                                                            }
                                                        }}
                                                        placeholder={t('reglement.selectClient')}
                                                        isClearable={!reglement && !isQuickPayment}
                                                        isSearchable={!reglement && !isQuickPayment}
                                                        isDisabled={!canEdit || isQuickPayment}
                                                        className={`${errors.clientId && touched.clientId ? 'is-invalid' : ''}`}
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={compactSelectStyles}
                                                    />
                                                    <ErrorMessage name="clientId" component="div" className="text-danger small mt-1" />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label className="fw-bold text-secondary small mb-1">
                                                        {t('reglement.chantier', 'Chantier')}
                                                    </Label>
                                                    <Select
                                                        options={chantierOptions}
                                                        value={chantierOptions.find(option => option.value === selectedChantier?.value) || null}
                                                        onChange={(option) => setSelectedChantier(option)}
                                                        placeholder={t('reglement.selectChantier', 'Sélectionner...')}
                                                        isClearable={!isQuickPayment}
                                                        isSearchable={!isQuickPayment}
                                                        className=""
                                                        menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                                                        styles={compactSelectStyles}
                                                        isDisabled={!canEdit || isQuickPayment}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup className="mb-0">
                                                    <Label for="companyId" className="fw-bold text-secondary small mb-1">
                                                        {t('company.company', 'Entreprise')} *
                                                    </Label>
                                                    <Input
                                                        type="select"
                                                        id="companyId"
                                                        name="companyId"
                                                        value={values.companyId || ''}
                                                        onChange={(e) => {
                                                            handleChange(e);
                                                            setSelectedCompanyId(e.target.value ? Number(e.target.value) : null);
                                                            setSelectedDocuments([]);
                                                            setIsCautionPayment(false);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                            setDocumentsInitialized(false);
                                                        }}
                                                        onBlur={handleBlur}
                                                        invalid={touched.companyId && errors.companyId}
                                                        disabled={isCompanyFieldDisabled}
                                                        bsSize="sm"
                                                    >
                                                        <option value="">
                                                            {isDocumentBasedReglement
                                                                ? t('company.companyAutoFromDocuments', 'Entreprise déterminée par les documents')
                                                                : t('company.selectCompany', 'Sélectionner une entreprise')}
                                                        </option>
                                                        {companies?.map((item) => (
                                                            <option key={item.id} value={item.id}>
                                                                {item.name}
                                                            </option>
                                                        ))}
                                                    </Input>
                                                    {touched.companyId && errors.companyId && (
                                                        <div className="invalid-feedback d-block">{errors.companyId}</div>
                                                    )}
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        {selectedClient && (
                                            <Row className="mt-2 g-2">
                                                <Col>
                                                    <Row className="g-2 align-items-stretch">
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <FaBalanceScale className="mb-1 text-success" size={16} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.balance')}</span>
                                                                    {selectedClient.totalCredit > selectedClient.totalDebit ? (
                                                                        <span className="fw-bold text-success" style={{ fontSize: '0.82rem' }}>+{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                                    ) : selectedClient.totalCredit < selectedClient.totalDebit ? (
                                                                        <span className="fw-bold text-danger" style={{ fontSize: '0.82rem' }}>-{formatPrice(Math.abs(selectedClient.solde))}</span>
                                                                    ) : (
                                                                        <span className="fw-bold text-muted" style={{ fontSize: '0.82rem' }}>{formatPrice(0)}</span>
                                                                    )}
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <FaArrowDown className="mb-1 text-danger" size={16} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.totalDebit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.82rem' }}>{formatPrice(selectedClient.totalDebit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={2}>
                                                            <Card className="shadow-sm h-100 bg-light border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <FaArrowUp className="mb-1 text-primary" size={16} />
                                                                    <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.totalCredit')}</span>
                                                                    <span className="fw-bold" style={{ fontSize: '0.82rem' }}>{formatPrice(selectedClient.totalCredit)}</span>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-warning bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaCalendarAlt className="me-1 text-warning" size={14} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.echeances')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-warning" style={{ fontSize: '0.82rem' }}>{echeances?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-warning" style={{ fontSize: '0.65rem' }} onClick={() => setShowEcheanceModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                        <Col xs={6} md={3}>
                                                            <Card className="shadow-sm h-100 bg-info bg-opacity-10 border-0 rounded-2">
                                                                <CardBody className="d-flex flex-column justify-content-center align-items-center py-2 px-2">
                                                                    <div className="d-flex align-items-center mb-1">
                                                                        <FaShieldAlt className="me-1 text-info" size={14} />
                                                                        <span className="text-muted text-uppercase" style={{ fontSize: '0.65rem' }}>{t('client.cautions')}</span>
                                                                    </div>
                                                                    <span className="fw-bold text-info" style={{ fontSize: '0.82rem' }}>{cautions?.length || 0}</span>
                                                                    <Button color="link" size="sm" className="p-0 fw-bold text-info" style={{ fontSize: '0.65rem' }} onClick={() => setShowCautionModal(true)}>
                                                                        {t('client.viewDetails')}
                                                                    </Button>
                                                                </CardBody>
                                                            </Card>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            </Row>
                                        )}
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                        {selectedClient && (
                            <Card className="payment-form-panel payment-form-left-item payment-form-documents-card mt-3">
                                <CardHeader className="payment-form-section-header d-flex justify-content-between align-items-center">
                                    <div className="fw-bold">
                                        <FaList className="me-2" />
                                        {t('reglement.documentsARegler')}
                                    </div>
                                    {canEdit && (
                                        <Button
                                            color="primary"
                                            size="sm"
                                            className="d-none"
                                            onClick={() => setShowDocumentModal(true)}
                                            disabled={!selectedClient}
                                        >
                                            {t('reglement.selectionnerDocuments', 'SÃ©lectionner les documents')}
                                        </Button>
                                    )}
                                </CardHeader>
                                <CardBody>
                                    <Row className="g-2 mb-3 align-items-end">
                                        <Col md={4}>
                                            <FormGroup className="mb-0">
                                                <Label className="mb-1">{t('common.search', 'Rechercher')}</Label>
                                                <div className="input-group input-group-sm">
                                                    <span className="input-group-text">
                                                        <FaSearch />
                                                    </span>
                                                    <Input
                                                        value={documentFilters.query}
                                                        placeholder={t('reglement.searchDocument', 'NÂ° document, rÃ©fÃ©rence, client')}
                                                        onChange={(e) => setDocumentFilters(prev => ({ ...prev, query: e.target.value }))}
                                                        disabled={!selectedClient || !canEdit}
                                                    />
                                                </div>
                                            </FormGroup>
                                        </Col>
                                        <Col md={3}>
                                            <FormGroup className="mb-0">
                                                <Label className="mb-1">{t('common.amount', 'Montant')}</Label>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    bsSize="sm"
                                                    value={documentFilters.amount}
                                                    placeholder={t('reglement.amountFilterPlaceholder', 'Montant restant min')}
                                                    onChange={(e) => setDocumentFilters(prev => ({ ...prev, amount: e.target.value }))}
                                                    disabled={!selectedClient || !canEdit}
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={5}>
                                            <div className="d-flex justify-content-md-end gap-3 small text-muted pt-4">
                                                <span>{filteredAvailableDocuments.length} {t('reglement.documents', 'documents')}</span>
                                                <span>{t('reglement.total')}: {formatPrice(filteredAvailableDocuments.reduce((sum, doc) => sum + getDocumentRemainingAmount(doc), 0), false)} DH</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    {!selectedClient ? (
                                        <div className="text-muted text-center py-3 border rounded mb-3">
                                            {t('reglement.selectClientFirst', 'SÃ©lectionnez un client pour afficher les documents')}
                                        </div>
                                    ) : documentLoading ? (
                                        <div className="text-center py-4 border rounded mb-3">
                                            <Spinner color="primary" size="sm" />
                                            <div className="mt-2 text-muted">{t('common.loading')}</div>
                                        </div>
                                    ) : (
                                        <div className="table-responsive mb-2 border rounded" style={{ maxHeight: 'calc(100vh - 430px)', overflowY: 'auto' }}>
                                            <Table bordered hover size="sm" className="mb-0">
                                                <thead className="table-light sticky-top">
                                                    <tr>
                                                        <th width="50">
                                                            <Input
                                                                type="checkbox"
                                                                checked={allFilteredDocumentsSelected}
                                                                disabled={!canEdit || selectableDocuments.length === 0}
                                                                onChange={(e) => {
                                                                    const checked = e.target.checked;
                                                                    if (checked) {
                                                                        setIsCautionPayment(false);
                                                                        setSelectedDocuments(prev => {
                                                                            const newDocuments = selectableDocuments.filter(document =>
                                                                                !prev.some(selected => selected.id === document.id)
                                                                            );
                                                                            const nextDocuments = [...prev, ...newDocuments];
                                                                            syncCompanyFromDocuments(nextDocuments, setFieldValue);
                                                                            syncMontantRegleFromDocuments(nextDocuments, setFieldValue);
                                                                            return nextDocuments;
                                                                        });
                                                                        return;
                                                                    }

                                                                    setSelectedDocuments(prev => {
                                                                        const nextDocuments = prev.filter(selected =>
                                                                            !selectableDocuments.some(document => document.id === selected.id)
                                                                        );
                                                                        syncCompanyFromDocuments(nextDocuments, setFieldValue);
                                                                        syncMontantRegleFromDocuments(nextDocuments, setFieldValue);
                                                                        return nextDocuments;
                                                                    });
                                                                }}
                                                            />
                                                        </th>
                                                        {renderDocumentSortHeader('Réf.', 'numero')}
                                                        {renderDocumentSortHeader(t('reglement.date'), 'date')}
                                                        {renderDocumentSortHeader('Montant', 'total')}
                                                        {renderDocumentSortHeader('Payé', 'paid')}
                                                        {renderDocumentSortHeader('Reste', 'remaining')}
                                                        {renderDocumentSortHeader(t('reglement.statut'), 'status')}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {filteredAvailableDocuments.length === 0 ? (
                                                        <tr>
                                                            <td colSpan={hasInvoiceDocuments ? '10' : '9'} className="text-center text-muted py-4">
                                                                {t('reglement.noDocumentsFound', 'Aucun document trouvÃ©')}
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        sortedAvailableDocuments.map((document) => {
                                                            const montantTotal = getDocumentTotalAmount(document);
                                                            const montantPaye = getDocumentPaidAmount(document);
                                                            const montantRestant = getDocumentRemainingAmount(document);
                                                            const isSelected = selectedDocuments.some(doc => doc.id === document.id);

                                                            return (
                                                                <tr key={document.id} className={isSelected ? 'table-active' : ''}>
                                                                    <td>
                                                                        <Input
                                                                            type="checkbox"
                                                                            checked={isSelected}
                                                                            disabled={!canEdit || montantRestant <= 0}
                                                                            onChange={(e) => toggleDocumentSelection(document, e.target.checked, setFieldValue)}
                                                                        />
                                                                    </td>

                                                                    <td className="fw-bold">{document.numero}</td>
                                                                    <td>{document.dateOperation || document.dateFacture || '-'}</td>
                                                                    <td>{formatPrice(montantTotal, false)} DH</td>
                                                                    <td>{formatPrice(montantPaye, false)} DH</td>
                                                                    <td className="fw-bold text-danger">{formatPrice(montantRestant, false)} DH</td>
                                                                    <td>{document.statut || document.status || '-'}</td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                    <div className="border rounded p-3 bg-light-subtle mb-2">
                                        <div className="d-flex flex-wrap justify-content-between gap-3">
                                            <div>
                                                <div className="small text-muted text-uppercase">
                                                    {t('reglement.documentsSelectionnes', 'Documents sÃ©lectionnÃ©s')}
                                                </div>
                                                <div className="fw-bold fs-5">{selectedDocuments.length}</div>
                                            </div>
                                            <div className="text-md-end">
                                                <div className="small text-muted text-uppercase">
                                                    {t('reglement.total', 'Total')}
                                                </div>
                                                <div className="fw-bold fs-5 text-primary">
                                                    {formatPrice(calculateTotalAmount(), false)} DH
                                                </div>
                                            </div>
                                        </div>
                                        {selectedDocuments.length === 0 && (
                                            <div className="text-muted small mt-2">
                                                {t('reglement.noDocumentsSelected', 'Aucun document sÃƒÂ©lectionnÃƒÂ©')}
                                            </div>
                                        )}
                                    </div>
                                    {false ? (
                                        <div className="text-muted text-center py-3">
                                            {t('reglement.noDocumentsSelected', 'Aucun document sÃ©lectionnÃ©')}
                                        </div>
                                    ) : (
                                        <div className="table-responsive d-none">
                                            <Table bordered hover size="sm" className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>{t('reglement.numero')}</th>
                                                        <th>{t('reglement.date')}</th>
                                                        <th>{t('reglement.montantTTC')}</th>
                                                        <th>{t('reglement.montantPaye')}</th>
                                                        <th>{t('reglement.montantRestant')}</th>
                                                        {canEdit && <th width="80">{t('common.actions', 'Actions')}</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedDocuments.map((document) => {
                                                        const montantTotal = document.montantTotalTTC ?? document.montantTTC ?? 0;
                                                        const montantPaye = document.montantRegle ?? document.montantPaye ?? 0;
                                                        const montantRestant = montantTotal - montantPaye;

                                                        return (
                                                            <tr key={document.id}>
                                                                <td className="fw-bold">{document.numero}</td>
                                                                <td>{document.createdAt || document.dateFacture || '-'}</td>
                                                                <td>{formatPrice(montantTotal, false)} DH</td>
                                                                <td>{formatPrice(montantPaye, false)} DH</td>
                                                                <td className="fw-bold text-danger">{formatPrice(montantRestant, false)} DH</td>
                                                                {canEdit && (
                                                                    <td>
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            className="text-danger p-0"
                                                                            onClick={() => removeDocument(document.id, setFieldValue)}
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    </td>
                                                                )}
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
	                                </CardBody>
	                            </Card>
	                        )}
                        </section>
                        <aside className="payment-side-pane">
                        {selectedClient && (
                            <Alert color={Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) >= calculateTotalAmount() ? 'info' : 'warning'} className="payment-form-right-item mt-3 mb-3">
                                <div className="fw-bold mb-1">Situation avant paiement</div>
                                <div className="small">
                                    {selectedDocuments.length} document(s) selectionne(s),
                                    reste a payer {formatPrice(calculateTotalAmount(), false)} DH.
                                    {loadingAdvances ? (
                                        <span className="ms-1">Verification des avances...</span>
                                    ) : totalAvailableAdvances > 0 && (
                                        <span className="text-success ms-1">
                                            Avances disponibles {formatPrice(totalAvailableAdvances, false)} DH,
                                            selectionnees {formatPrice(calculateAdvanceUsageAmount(selectedDocuments), false)} DH.
                                        </span>
                                    )}
                                    Montant saisi {formatPrice(Number(values.montantRegle || 0), false)} DH.
                                    {Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) > calculateTotalAmount() && (
                                        <span className="text-success ms-1">
                                            Le surplus de {formatPrice(Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) - calculateTotalAmount(), false)} DH restera en {isCautionPayment ? 'caution client' : 'avance client'}.
                                        </span>
                                    )}
                                    {Number(values.montantRegle || 0) + calculateAdvanceUsageAmount(selectedDocuments) < calculateTotalAmount() && (
                                        <span className="text-warning ms-1">
                                            Paiement partiel: {formatPrice(calculateTotalAmount() - Number(values.montantRegle || 0) - calculateAdvanceUsageAmount(selectedDocuments), false)} DH restera impaye.
                                        </span>
                                    )}
                                </div>
                            </Alert>
                        )}
                        {selectedClient && (
                            <Card className="payment-form-panel payment-form-right-item mb-3">
                                <CardHeader className="payment-form-section-header py-2">
                                    <h6 className="mb-0">{t('reglement.avancesDisponibles', 'Avances disponibles')}</h6>
                                </CardHeader>
                                <CardBody className="py-2">
                                    <div className="small text-muted mb-2">
                                        {selectedDocuments.length > 0
                                            ? t('reglement.selectAvancesToUse', 'Cochez les avances à utiliser pour ces documents.')
                                            : t('reglement.availableAdvancesForCompany', 'Avances disponibles pour cette entreprise.')}
                                    </div>
                                    {loadingAdvances ? (
                                        <div className="text-center py-2">
                                            <Spinner size="sm" color="primary" />
                                            <span className="ms-2 small text-muted">Chargement des avances...</span>
                                        </div>
                                    ) : !selectedCompanyId ? (
                                        <div className="small text-muted py-2">
                                            {t('company.selectCompany', 'Sélectionner une entreprise')}
                                        </div>
                                    ) : availableAdvances.length === 0 ? (
                                        <div className="small text-muted py-2">
                                            {t('reglement.noAvailableAdvancesForCompany', 'Aucune avance disponible pour cette entreprise.')}
                                        </div>
                                    ) : (
                                        <div className="table-responsive">
                                            <Table size="sm" className="mb-0 align-middle">
                                                <tbody>
                                                    {availableAdvances.map((avance) => {
                                                        const checked = selectedAdvanceIds.includes(avance.id);
                                                        return (
                                                            <tr key={avance.id}>
                                                                <td style={{ width: 34 }}>
                                                                    <Input
                                                                        type="checkbox"
                                                                        checked={checked}
                                                                        disabled={!canEdit || selectedDocuments.length === 0}
                                                                        onChange={(event) => {
                                                                            setSelectedAdvanceIds(prev => event.target.checked
                                                                                ? [...prev, avance.id]
                                                                                : prev.filter(id => id !== avance.id));
                                                                        }}
                                                                    />
                                                                </td>
                                                                <td>
                                                                    <div className="fw-bold">{avance.referenceReglement || `Avance #${avance.id}`}</div>
                                                                    <div className="text-muted small">{avance.dateCreation || '-'}</div>
                                                                </td>
                                                                <td className="text-end fw-bold text-success">
                                                                    {formatPrice(avance.montantDisponible, false)} DH
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </Table>
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        )}
	                        <Card className="payment-form-panel payment-form-right-item payment-form-details-card">
                            <CardHeader className="payment-form-section-header">
                                <h5 className="mb-0">
                                    <FaEdit className="me-2" />
                                    {t('reglement.detailsReglement')}
                                </h5>
                            </CardHeader>

                            <CardBody>
                                <Row className="g-2 payment-form-details-grid">
                                    <Col md={12}>
                                        <FormGroup className="payment-amount-control">
                                            <Label for="montantRegle">
                                                {t('reglement.montantRegle')} *
                                            </Label>
                                            <Field
                                                name="montantRegle"
                                                type="number"
                                                onFocus={(e) => {
                                                    e.target.select();
                                                    e.target.addEventListener('wheel', preventScroll, { passive: false });
                                                }}
                                                onBlur={(e) => {
                                                    e.target.removeEventListener('wheel', preventScroll);
                                                }}

                                                className={`form-control ${errors.montantRegle && touched.montantRegle ? 'is-invalid' : ''}`}
                                                placeholder="0.00"
                                                value={values.montantRegle}
                                                onChange={(e) => {
                                                    if (canEdit) {
                                                        setFieldValue('montantRegle', e.target.value);
                                                    }
                                                }}
                                                disabled={!canEdit}
                                            />
                                            <ErrorMessage name="montantRegle" component="div" className="invalid-feedback" />
                                            <div className="payment-form-field-hint">
                                                Rempli automatiquement selon les documents selectionnes, mais reste modifiable.
                                            </div>


                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="dateReglement">
                                                <FaCalendarAlt className="me-1" />
                                                {t('reglement.dateReglement')} *
                                            </Label>
                                            <Field
                                                name="dateReglement"
                                                type="date"
                                                className={`form-control ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`}
                                                disabled={!canEdit}
                                            />
                                            <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label for="dateEncaissement">
                                                <FaCalendarAlt className="me-1" />
                                                {t('reglement.dateEncaissement')}
                                            </Label>
                                            <Field
                                                name="dateEncaissement"
                                                type="date"
                                                className="form-control"
                                                disabled={!canEdit}
                                            />
                                        </FormGroup>
                                    </Col>
                                    {selectedDocuments.length === 0 && (
                                        <Col md={12}>
                                            <FormGroup check className="payment-caution-option">
                                                <Input
                                                    id="isCautionPayment"
                                                    type="checkbox"
                                                    checked={isCautionPayment}
                                                    disabled={!canEdit}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setIsCautionPayment(checked);
                                                        setTypeReglement(checked ? 'CAUTION' : 'AVANCE');
                                                        if (checked) {
                                                            setSelectedDocuments([]);
                                                            setDocumentFilters({ query: '', amount: '' });
                                                        }
                                                    }}
                                                />
                                                <Label check for="isCautionPayment">
                                                    {t('reglement.caution', 'Caution')}
                                                </Label>
                                                <div className="payment-form-field-hint">
                                                    Sans document selectionne, le paiement est une avance par defaut. Cochez pour le saisir comme caution.
                                                </div>
                                            </FormGroup>
                                        </Col>
                                    )}
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label for="modeReglement">{t('reglement.modeReglement')} *</Label>
                                            <Field
                                                as="select"
                                                name="modeReglement"
                                                className={`form-control ${errors.modeReglement && touched.modeReglement ? 'is-invalid' : ''}`}
                                                disabled={!canEdit}
                                            >
                                                <option value="">{t('reglement.selectModeReglement')}</option>
                                                {modeReglementOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </Field>
                                            <ErrorMessage name="modeReglement" component="div" className="invalid-feedback" />
                                        </FormGroup>
                                    </Col>
                                    {(['CHEQUE', 'TRAITE'].includes(values.modeReglement)) && (
                                        <>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="numeroCheque">
                                                        {values.modeReglement === 'TRAITE'
                                                            ? t('reglement.numeroTraite', 'Numero traite')
                                                            : t('reglement.numeroCheque')}
                                                    </Label>
                                                    <Field
                                                        name="numeroCheque"
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={values.modeReglement === 'TRAITE'
                                                            ? t('reglement.numeroTraite', 'Numero traite')
                                                            : t('reglement.numeroCheque')}
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="banque">{t('reglement.banque')}</Label>
                                                    <Input
                                                        type="select"
                                                        id="banque"
                                                        name="banque"
                                                        value={values.banque}
                                                        onChange={handleChange}
                                                        onBlur={handleBlur}
                                                        invalid={touched.banque && errors.banque}
                                                        disabled={!canEdit}
                                                    >
                                                        <option value="">{t('payment.selectBank', 'SÃ©lectionner une banque')}</option>
                                                        <option value="BMCE">BMCE</option>
                                                        <option value="ATTIJARI">ATTIJARI</option>
                                                        <option value="CIH">CIH</option>
                                                        <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                                                        <option value="SG">SG</option>
                                                    </Input>
                                                </FormGroup>
                                            </Col>
                                            <Col md={4}>
                                                <FormGroup>
                                                    <Label for="dateEcheance">
                                                        <FaCalendarAlt className="me-1" />
                                                        {t('reglement.dateEcheance')}
                                                    </Label>
                                                    <Field
                                                        name="dateEcheance"
                                                        type="date"
                                                        className="form-control"
                                                        disabled={!canEdit}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </>

                                    )}
                                    {(values.modeReglement === 'VIREMENT') && (
                                        <Col md={12}>
                                            <FormGroup>
                                                <Label for="referencePaiement">{t('reglement.referencePaiement')}</Label>
                                                <Field
                                                    name="referencePaiement"
                                                    type="text"
                                                    className="form-control"
                                                    disabled={!canEdit}
                                                />
                                            </FormGroup>
                                        </Col>
                                    )}
                                </Row>



                                <Row className="mt-3">
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label for="commentaire">{t('reglement.commentaire')}</Label>
                                            <Field
                                                as="textarea"
                                                name="commentaire"
                                                rows="3"
                                                className="form-control"
                                                placeholder={t('reglement.commentairePlaceholder')}
                                                disabled={!canEdit}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>




                                {/* Allocations Table (Edit Mode) */}
                                {reglement && Array.isArray(reglement.allocations) && reglement.allocations.length > 0 && (
                                    <Card className="mt-3 border-info">
                                        <CardHeader className="bg-info text-white py-2">
                                            <h6 className="mb-0">{t('reglement.allocationsPayments', 'Affectations du réglement')}</h6>
                                        </CardHeader>
                                        <CardBody>
                                            <div className="table-responsive">
                                                <Table bordered hover size="sm">
                                                    <thead className="table-info">
                                                        <tr>
                                                            <th>{t('reglement.documentNumber', 'Document')}</th>
                                                            <th>{t('reglement.montantDocument', 'Montant')}</th>
                                                            <th>{t('reglement.montantAllouex', 'Payé')}</th>
                                                            <th>{t('reglement.montantRestantx', 'Reste')}</th>
                                                            <th>{t('reglement.commentairex', 'Note')}</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {reglement.allocations.map(allocation => (
                                                            <tr key={allocation.id}>
                                                                <td>
                                                                    <div className="fw-semibold">{allocation.numeroDocument}</div>
                                                                    <small className="text-muted">
                                                                        {allocation.sourcePaiement === 'AVANCE' ? 'Avance' : 'Paiement'}
                                                                        {' '}
                                                                        {allocation.referenceSourcePaiement || allocation.refReglement || ''}
                                                                    </small>
                                                                </td>
                                                                <td>{formatPrice(allocation.montantDocument)}</td>
                                                                <td className="fw-bold text-success">{formatPrice(allocation.montantAlloue)}</td>
                                                                <td className="fw-bold text-danger">{formatPrice(allocation.montantRestant)}</td>
                                                                <td>{allocation.commentaire || '-'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr>
                                                            <td className="fw-bold text-end">{t('reglement.total', 'Total')}:</td>
                                                            <td className="fw-bold">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantDocument, 0))}</td>
                                                            <td className="fw-bold text-success">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantAlloue, 0))}</td>
                                                            <td className="fw-bold text-danger">{formatPrice(reglement.allocations.reduce((acc, allocation) => acc + allocation.montantRestant, 0))}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>
                                        </CardBody>
                                    </Card>
                                )}
                            </CardBody>
                        </Card>
                        {/* Boutons d'action */}
                        <Card className="payment-form-panel payment-form-right-item payment-form-actionbar mt-1 d-none">
                            <CardBody>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        {selectedDocuments.length > 0 && (
                                            <small className="text-muted">
                                                {selectedDocuments.length} {t('reglement.documentsSelectionnes')} -
                                                {t('reglement.total')}: <strong>{formatPrice(calculateTotalAmount())}</strong>
                                            </small>
                                        )}
                                    </div>
                                    {
                                        canEdit && <div className="d-flex gap-2">

                                            <Button
                                                type="submit"
                                                color="primary"
                                                className="payment-primary-action"
                                                disabled={
                                                    isSubmitting ||
                                                    (!selectedClient) ||
                                                    ((!values.montantRegle || values.montantRegle <= 0) && calculateAdvanceUsageAmount(selectedDocuments) <= 0)
                                                }
                                            >
                                                {isSubmitting && <Spinner size="sm" className="me-2" />}
                                                <i className={`fas ${reglement ? 'fa-save' : 'fa-plus'} me-1`}></i>
                                                {reglement ? t('common.update') : t('common.create')}
                                            </Button>


                                        </div>}
                                </div>
                            </CardBody>
                        </Card>
                        </aside>
                        </div>
                    </Form>
                )}
            </Formik>

            {/* Modal de sÃ©lection des documents */}
            <DocumentSelectionModal
                isOpen={showDocumentModal}
                toggle={() => setShowDocumentModal(false)}
                typeDocument={typeReglement}
                documents={availableDocuments}
                loading={documentLoading}
                selectedDocuments={selectedDocuments}
                onDocumentsSelected={(documents) => handleDocumentsSelected(documents)}
            />
            <Modal isOpen={showEcheanceModal} toggle={() => setShowEcheanceModal(false)} size="lg">
                <ModalHeader toggle={() => setShowEcheanceModal(false)} className="bg-warning bg-opacity-10 border-0">
                    <FaCalendarAlt className="me-2 text-warning" />
                    <span className="fw-bold text-warning">{t('client.echeancesDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {echeances && echeances.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-warning fs-5">
                                    <FaCalendarAlt className="me-2" /> {t('client.echeances')}: {echeances.length}
                                </span>
                                <span className="fw-bold text-muted">{t('echeance.total')}: <span className="text-warning">{formatPrice(getTotalMontant(echeances))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-warning bg-opacity-10">
                                    <tr>
                                        <th>{t('echeance.reference')}</th>
                                        <th>{t('echeance.dateReglement')}</th>
                                        <th>{t('echeance.numeroCheque')}</th>
                                        <th>{t('echeance.banque')}</th>
                                        <th>{t('echeance.montantCheque')}</th>
                                        <th>{t('echeance.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {echeances.map((echeance, idx) => (
                                        <tr key={idx}>
                                            <td>{echeance.reference}</td>
                                            <td>{echeance.dateReglement}</td>
                                            <td>{echeance.numeroCheque}</td>
                                            <td>{echeance.banque}</td>
                                            <td className="text-end fw-bold text-warning">{formatPrice(echeance.montantRegle)}</td>
                                            <td>{echeance.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noEcheances')}</div>
                    )}
                </ModalBody>
            </Modal>
            <Modal isOpen={showCautionModal} toggle={() => setShowCautionModal(false)} size="lg">
                <ModalHeader toggle={() => setShowCautionModal(false)} className="bg-info bg-opacity-10 border-0">
                    <FaShieldAlt className="me-2 text-info" />
                    <span className="fw-bold text-info">{t('client.cautionsDetails')}</span>
                </ModalHeader>
                <ModalBody>
                    {cautions && cautions.length > 0 ? (
                        <div>
                            <div className="mb-3 d-flex justify-content-between align-items-center">
                                <span className="fw-bold text-info fs-5">
                                    <FaShieldAlt className="me-2" /> {t('client.cautions')}: {cautions.length}
                                </span>
                                <span className="fw-bold text-muted">{t('caution.total')}: <span className="text-info">{formatPrice(getTotalMontant(cautions))}</span></span>
                            </div>
                            <Table bordered hover responsive className="table-sm">
                                <thead className="bg-info bg-opacity-10">
                                    <tr>
                                        <th>{t('caution.reference')}</th>
                                        <th>{t('caution.dateReglement')}</th>
                                        <th>{t('caution.numeroCheque')}</th>
                                        <th>{t('caution.banque')}</th>
                                        <th>{t('caution.montantCheque')}</th>
                                        <th>{t('caution.dateEcheance')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cautions.map((caution, idx) => (
                                        <tr key={idx}>
                                            <td>{caution.reference}</td>
                                            <td>{caution.dateReglement}</td>
                                            <td>{caution.numeroCheque}</td>
                                            <td>{caution.banque}</td>
                                            <td className="text-end fw-bold text-info">{formatPrice(caution.montantRegle)}</td>
                                            <td>{caution.dateEcheance}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-muted text-center py-4">{t('client.noCautions')}</div>
                    )}
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default ReglementForm;


